import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatMoney } from '../ui/format';
import { Badge, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { ConfirmAction } from '../ui/ConfirmAction';
import { confirmPayment, listAdminInvoices, voidInvoice, type AdminInvoice } from './api';

/**
 * Invoices, from Safe Cortex's side.
 *
 * The work this screen exists for is one thing: money arrived, somebody confirms it. With a manual
 * provider that is the entire collection process, and doing it here — with a reason, audited, and
 * reviving the account in the same action — is the difference between a billing system and a
 * spreadsheet.
 *
 * Both actions take a reason, like every other control-plane action, because "somebody marked this
 * paid" is a question that gets asked.
 */

const TONE: Record<string, BadgeTone> = {
  OPEN: 'warning',
  PAID: 'positive',
  VOID: 'neutral',
  UNCOLLECTIBLE: 'danger',
  DRAFT: 'neutral',
};

const METHODS = ['PIX', 'TRANSFER', 'BOLETO', 'CARD', 'OTHER'];

type Action =
  { kind: 'pay'; invoice: AdminInvoice } | { kind: 'void'; invoice: AdminInvoice } | null;

export function InvoicesPanel({ tenantId }: { tenantId?: string }): React.JSX.Element {
  const client = useQueryClient();

  const [status, setStatus] = useState('OPEN');
  const [action, setAction] = useState<Action>(null);
  const [method, setMethod] = useState('PIX');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invoices = useQuery({
    queryKey: ['admin-invoices', tenantId, status],
    queryFn: () =>
      listAdminInvoices({ ...(tenantId ? { tenantId } : {}), ...(status ? { status } : {}) }),
  });

  const run = useMutation({
    mutationFn: (reason: string) => {
      if (action?.kind === 'pay') {
        return confirmPayment(action.invoice.id, {
          method,
          reference: reference.trim() || null,
          reason,
        });
      }

      if (action?.kind === 'void') return voidInvoice(action.invoice.id, reason);

      return Promise.resolve(undefined);
    },
    onSuccess: () => {
      setAction(null);
      setReference('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['admin-invoices'] });
      void client.invalidateQueries({ queryKey: ['admin-tenant'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const rows = invoices.data?.invoices ?? [];
  const open = rows.filter((invoice) => invoice.status === 'OPEN');
  const owed = open.reduce((sum, invoice) => sum + invoice.amount_cents, 0);

  const columns: Column<AdminInvoice>[] = [
    {
      key: 'number',
      header: t('billing.invoiceNumber'),
      render: (invoice) => `#${invoice.number}`,
    },
    {
      key: 'period',
      header: t('billing.period'),
      text: (invoice) => `${invoice.period_start} ${invoice.period_end}`,
      render: (invoice) => (
        <span className="muted small">
          {formatDate(invoice.period_start)} — {formatDate(invoice.period_end)}
        </span>
      ),
    },
    {
      key: 'amount',
      header: t('billing.amount'),
      align: 'end',
      text: (invoice) => String(invoice.amount_cents),
      render: (invoice) => (
        <span className="strong">{formatMoney(invoice.amount_cents, invoice.currency)}</span>
      ),
    },
    {
      key: 'due_at',
      header: t('billing.dueAt'),
      render: (invoice) => <span className="muted small">{formatDate(invoice.due_at)}</span>,
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      text: (invoice) => t(`billing.invoice.${invoice.status}`),
      render: (invoice) => (
        <Badge tone={TONE[invoice.status] ?? 'neutral'}>
          {t(`billing.invoice.${invoice.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      text: () => '',
      render: (invoice) => (
        <>
          {invoice.status === 'OPEN' && (
            <span className="cell-inline">
              <button
                type="button"
                className="button button--primary button--compact"
                onClick={() => setAction({ kind: 'pay', invoice })}
              >
                {t('admin.confirmPayment')}
              </button>
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => setAction({ kind: 'void', invoice })}
              >
                {t('admin.voidInvoice')}
              </button>
            </span>
          )}

          {invoice.status === 'PAID' && invoice.payment_method && (
            <span className="muted small">{invoice.payment_method}</span>
          )}
        </>
      ),
    },
    // Off by default: what somebody needs only when reconciling one specific payment.
    {
      key: 'paid_at',
      header: t('billing.paidAt'),
      hidden: true,
      render: (invoice) => formatDate(invoice.paid_at),
    },
    {
      key: 'payment_reference',
      header: t('admin.paymentReference'),
      hidden: true,
      render: (invoice) => invoice.payment_reference ?? '—',
    },
    {
      key: 'plan_key',
      header: t('billing.plan'),
      hidden: true,
      render: (invoice) => invoice.plan_key,
    },
  ];

  return (
    <Panel
      title={t('admin.invoices')}
      actions={
        <select
          className="select select--compact"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          aria-label={t('crm.field.status')}
        >
          <option value="">{t('admin.allInvoices')}</option>
          <option value="OPEN">{t('billing.invoice.OPEN')}</option>
          <option value="PAID">{t('billing.invoice.PAID')}</option>
          <option value="VOID">{t('billing.invoice.VOID')}</option>
        </select>
      }
    >
      {open.length > 0 && (
        <p className="muted small">
          {t('admin.owed')
            .replace('{n}', String(open.length))
            .replace('{total}', formatMoney(owed))}
        </p>
      )}

      <DataTable
        tableKey="admin-invoices"
        rows={invoices.data?.invoices}
        columns={columns}
        rowKey={(invoice) => invoice.id}
        isLoading={invoices.isLoading}
        error={invoices.error}
        onRetry={() => void invoices.refetch()}
        empty={{ title: t('admin.noInvoices') }}
      />

      <ConfirmAction
        open={action?.kind === 'pay'}
        title={t('admin.confirmPaymentTitle')}
        description={
          action?.kind === 'pay'
            ? t('admin.confirmPaymentBody').replace(
                '{amount}',
                formatMoney(action.invoice.amount_cents, action.invoice.currency),
              )
            : undefined
        }
        confirmLabel={t('admin.confirmPayment')}
        busy={run.isPending}
        error={error}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => run.mutate(reason)}
      >
        <label className="stack__field">
          <span>{t('admin.paymentMethod')}</span>
          <select
            className="select"
            value={method}
            onChange={(event) => setMethod(event.target.value)}
          >
            {METHODS.map((each) => (
              <option key={each} value={each}>
                {t(`admin.method.${each}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="stack__field">
          <span>{t('admin.paymentReference')}</span>
          <input
            value={reference}
            placeholder={t('admin.paymentReferenceHint')}
            onChange={(event) => setReference(event.target.value)}
          />
        </label>
      </ConfirmAction>

      <ConfirmAction
        open={action?.kind === 'void'}
        tone="danger"
        title={t('admin.voidInvoiceTitle')}
        description={t('admin.voidInvoiceBody')}
        confirmLabel={t('admin.voidInvoice')}
        busy={run.isPending}
        error={error}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => run.mutate(reason)}
      />
    </Panel>
  );
}
